import { supabase } from '@/lib/supabase';

// =========================================
// Queries (read operations)
// =========================================

/** Fetch teams owned by a user */
export async function fetchTeamsByOwner(userId: string) {
  return supabase
    .from('teams')
    .select('*')
    .eq('owner_id', userId);
}

/** Fetch accepted team memberships for a user (returns team_id list) */
export async function fetchAcceptedMemberships(userId: string) {
  return supabase
    .from('team_members')
    .select('team_id')
    .eq('user_id', userId)
    .not('accepted_at', 'is', null);
}

/** Fetch teams by IDs */
export async function fetchTeamsByIds(teamIds: string[]) {
  return supabase
    .from('teams')
    .select('*')
    .in('id', teamIds);
}

/** Count members of a team */
export async function countTeamMembers(teamId: string) {
  return supabase
    .from('team_members')
    .select('*', { count: 'exact', head: true })
    .eq('team_id', teamId);
}

/** Fetch a single team by ID */
export async function fetchTeamById(teamId: string) {
  return supabase
    .from('teams')
    .select('*')
    .eq('id', teamId)
    .single();
}

/** Fetch team members with their profiles */
export async function fetchTeamMembersWithProfiles(teamId: string) {
  return supabase
    .from('team_members')
    .select('*, profiles(email, full_name, avatar_url)')
    .eq('team_id', teamId)
    .order('invited_at', { ascending: true });
}

/** Fetch pending invitations for a user (with team data) */
export async function fetchPendingInvitations(userId: string) {
  return supabase
    .from('team_members')
    .select('*, teams(*)')
    .eq('user_id', userId)
    .is('accepted_at', null);
}

// =========================================
// Mutations (write operations)
// =========================================

/** Create a new team */
export async function insertTeam(name: string, ownerId: string) {
  return supabase
    .from('teams')
    .insert({ name, owner_id: ownerId })
    .select()
    .single();
}

/** Add a member to a team */
export async function insertTeamMember(params: {
  team_id: string;
  user_id: string;
  role: string;
  accepted_at: string;
}) {
  return supabase.from('team_members').insert(params);
}

/** Update a team's name */
export async function updateTeamName(teamId: string, name: string) {
  return supabase
    .from('teams')
    .update({ name })
    .eq('id', teamId)
    .select()
    .single();
}

/** Delete a team */
export async function deleteTeam(teamId: string) {
  return supabase
    .from('teams')
    .delete()
    .eq('id', teamId);
}

/** Update a team member's role */
export async function updateMemberRole(memberId: string, role: string) {
  return supabase
    .from('team_members')
    .update({ role })
    .eq('id', memberId);
}

/** Remove a team member */
export async function removeTeamMember(memberId: string) {
  return supabase
    .from('team_members')
    .delete()
    .eq('id', memberId);
}

/** Accept a team invitation */
export async function acceptInvitation(teamId: string, userId: string) {
  return supabase
    .from('team_members')
    .update({ accepted_at: new Date().toISOString() })
    .eq('team_id', teamId)
    .eq('user_id', userId)
    .is('accepted_at', null);
}
